import { ApiRequest, Recaptcha } from "@plinknz/tah-website-elements";
import * as React from "react";
import * as SurveyType from "survey-react";
import { Loader } from "../../loader";
import { Survey } from "../../survey";
import { MESSAGE_ENDPOINT } from "../../../config/forms/contact";
import { MessagePayload } from "../../../config/forms/contact/payload";
import { contactApi } from "../../../service/forms/contact/api";
import { createSurveyConfig } from "../../../service/forms/contact/survey-config";
import { applySurveyConfig } from "../../../utility/apply-survey-config";

export interface ContactProps {
  request?: ApiRequest;
}
let payload: MessagePayload = null;

export const ContactForm = ({ request = contactApi }: ContactProps) => {
  const [surveyModel, updateSurvey] = React.useState<
    SurveyType.ReactSurveyModel
  >(null);
  const [surveyLoading, updateSurveyLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>(null);
  const [recaptchaLoading, updateRecaptchaLoading] = React.useState<boolean>(
    true
  );

  const handleRecaptcha = async (token: string) => {
    payload = {
      ...payload,
      recaptcha: token,
    };

    await request.post(MESSAGE_ENDPOINT, payload);
  };

  const sendDataToServer = async (survey: SurveyType.ReactSurveyModel) => {
    updateSurveyLoading(true);

    try {
      grecaptcha.execute();
      payload = {
        data: survey.data,
        recaptcha: undefined,
        tenant: process.env.TAH_TENANT,
      };
    } catch (sendError) {
      console.error(sendError);

      if (sendError instanceof Error) {
        setError(sendError.message);
      }
    } finally {
      updateSurveyLoading(false);
    }
  };

  const recaptchaLoaded = () => updateRecaptchaLoading(false);

  React.useEffect(() => {
    (async () => {
      if (recaptchaLoading || surveyLoading || error || surveyModel) {
        return;
      }

      try {
        updateSurveyLoading(true);
        const survey = await import(
          /* webpackChunkName: "survey-react" */ "survey-react"
        );
        await import(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          /* webpackChunkName: "surveyjs-widgets" */ "surveyjs-widgets" as any
        );

        // TODO: The run time imports above make it difficult to test
        // this without getting not wrapped in act errors
        // Need to figure out how to do this

        applySurveyConfig(survey);
        survey.surveyLocalization.locales.my.completingSurvey =
          "Tēnā Koe - Thanks for contacting us, we'll get back to you shortly";

        const result = await createSurveyConfig();
        const surveyInstance = new survey.Model(result);
        surveyInstance.locale = "my";

        updateSurvey(surveyInstance);
      } catch (fetchError) {
        console.warn("Error: ", fetchError);
        setError(
          fetchError ||
            "There was an error trying to retrieve information from the server. Please reload and try again."
        );
      } finally {
        updateSurveyLoading(false);
      }
    })();

    return () => {
      if (surveyModel) surveyModel.clear();
      updateSurvey(null);
    };
  }, [recaptchaLoading]);

  return (
    <div
      className="customer-form || constrain-width"
      data-testid="contact-form"
    >
      {((recaptchaLoading || surveyLoading) && (
        <div className="splash">
          <Loader size="large" />
        </div>
      )) ||
        (error && (
          <div data-testid="error">
            <p>{error}</p>
          </div>
        )) || <Survey model={surveyModel} onComplete={sendDataToServer} />}
      <Recaptcha onReady={recaptchaLoaded} onSubmit={handleRecaptcha} />
    </div>
  );
};
